import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { getImageAssetPath } from '@/lib/imageHelper';
import OptimizedImage from '@/components/common/OptimizedImage';

const TrustSection = () => {
  const { t } = useTranslation();

  // Partner- und Zertifikatslogos
  const microsoftPartnerLogo = getImageAssetPath('microsoft-partner.png');
  const datenschutzLogo = getImageAssetPath('dsgvo-compliant.png');
  const iso27001Logo = getImageAssetPath('iso-27001.png');

  const trustElements = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: '30 Tage Geld-zurück-Garantie',
      description: 'Teste Salescockpit risikofrei: Wenn du nicht vollständig zufrieden bist, erhältst du dein Geld zurück – ohne Wenn und Aber.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      title: 'Höchste Datensicherheit',
      description: 'Deine Daten werden in ISO-27001-zertifizierten deutschen Rechenzentren gespeichert und sind DSGVO-konform geschützt.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Schnelle Implementierung',
      description: 'Wir garantieren eine effiziente Implementierung innerhalb des vereinbarten Zeitrahmens – ohne versteckte Kosten.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: 'Support-Garantie',
      description: 'Unser Support-Team steht dir werktags von 8-18 Uhr zur Verfügung – mit garantierter Reaktionszeit von maximal 4 Stunden.'
    }
  ];

  const partnerLogos = [
    {
      src: microsoftPartnerLogo,
      alt: 'Microsoft Partner',
      width: 140,
      height: 60
    },
    {
      src: datenschutzLogo,
      alt: 'DSGVO konform',
      width: 140,
      height: 60
    },
    {
      src: iso27001Logo,
      alt: 'ISO 27001 zertifiziert',
      width: 140,
      height: 60
    }
  ];

  const customerLogos = [
    { name: 'LogoTechnik GmbH' },
    { name: 'Industrial AG' },
    { name: 'BerlinMetall' },
    { name: 'DataSystems' },
    { name: 'HamburgIT' }
  ];

  return (
    <section id="trust" className="py-20 bg-blue-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-blue-900">
            Vertrauen ist die Basis unserer Zusammenarbeit
          </h2>
          <p className="text-xl text-gray-700">
            Wir setzen auf langfristige Partnerschaften und garantieren dir maximale Sicherheit und Zufriedenheit.
          </p>
        </div>

        {/* Garantien */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {trustElements.map((element, index) => (
            <div 
              key={index}
              className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="flex justify-center mb-5">
                {element.icon}
              </div>
              <h3 className="text-xl font-bold mb-3 text-center text-gray-900">
                {element.title}
              </h3>
              <p className="text-gray-700 text-center">
                {element.description}
              </p>
            </div>
          ))}
        </div>

        {/* Partner und Zertifikate */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-16">
          <h3 className="text-2xl font-bold mb-8 text-center text-gray-900">
            Partner & Zertifizierungen
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {partnerLogos.map((logo, index) => (
              <div key={index} className="w-28 h-28 flex items-center justify-center">
                <OptimizedImage
                  src={logo.src}
                  alt={logo.alt}
                  width={logo.width}
                  height={logo.height}
                  className="max-w-full max-h-full"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Unternehmen, die Salescockpit vertrauen */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold mb-8 text-center text-gray-900">
            Diese Unternehmen vertrauen bereits auf Salescockpit
          </h3>
          <div className="flex flex-wrap justify-center gap-6">
            {customerLogos.map((logo, index) => (
              <div key={index} className="px-5 py-3 bg-white rounded-lg shadow">
                <span className="text-base font-medium text-gray-800">{logo.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Abschließender CTA */}
        <div className="max-w-3xl mx-auto text-center mt-16">
          <div className="p-8 bg-blue-600 rounded-xl shadow-lg">
            <h3 className="text-2xl font-bold mb-4 text-white">
              Bereit, deinen Außendienst zu revolutionieren?
            </h3>
            <p className="text-lg text-blue-100 mb-8">
              Mit Salescockpit steigerst du Effizienz, reduzierst Fehler und erhöhst den Umsatz – risikofrei dank unserer Garantien.
            </p>
            <a 
              href="#contact"
              className="inline-block px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-blue-50 transition-colors"
            >
              Jetzt Demo vereinbaren
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection; 